import { useRef, useState } from "react";
import Typewriter from "typewriter-effect";
import type { TypewriterClass } from "typewriter-effect";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import styles from "./AnimatedHeadline.module.scss";
import { Button } from "../Button/Button";
import { LinkType } from "@/types/baseTypes";

export type AnimatedHeadlineProps = {
  headline: string;
  elements: { text: string }[];
  link_404: LinkType | undefined;
  link_404_aria_label: string | undefined;
};

export function AnimatedHeadline({
  headline,
  elements,
  link_404,
  link_404_aria_label,
}: AnimatedHeadlineProps) {
  const { getLabel } = useGlobalContext();
  const instanceRef = useRef<TypewriterClass | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  const strings = elements.map((element) => element.text).filter(Boolean);

  const handlePauseToggle = () => {
    if (isPaused) {
      instanceRef.current?.start();
    } else {
      instanceRef.current?.pause();
    }
    setIsPaused(!isPaused);
  };

  return (
    <>
      <h1
        className={removeExtraSpacesFromClassNames(
          `h1-pages ${styles["animated-headline"]}`,
        )}
      >
        <span>{headline}</span>
        {strings.length > 0 && (
          <>
            <Typewriter
              component="span"
              onInit={(typewriter) => {
                instanceRef.current = typewriter;
              }}
              options={{
                strings,
                autoStart: true,
                loop: true,
                delay: 100,
                deleteSpeed: 50,
              }}
            />
          </>
        )}
      </h1>
      {link_404 ? (
        <Button
          type="primary"
          href={link_404.url || "/"}
          title={link_404.title || "Home"}
          aria-label={link_404_aria_label}
          target="_self"
        >
          <strong>{link_404.title || "Home"}</strong>
        </Button>
      ) : (
        <p>No Back-Link Added in Backend: Theme Options</p>
      )}
      <button
        type="button"
        className={removeExtraSpacesFromClassNames(
          `button-reset ${styles["pause-btn"]}`,
        )}
        onClick={handlePauseToggle}
        aria-label={
          isPaused
            ? getLabel("animation_play", "Play animation")
            : getLabel("animation_stop", "Pause animation")
        }
      >
        {isPaused
          ? getLabel("animation_play", "Play animation")
          : getLabel("animation_stop", "Pause animation")}
      </button>
    </>
  );
}
